import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import SafeDeleteDialog from "../../../components/ui/SafeDeleteDialog";
import {
  useCompanyAddresses,
  useCompanyDetails,
  useDeleteCompanyAddress,
  useSaveCompanyAddress,
  useSaveCompanyDetails,
} from "../../../api/queries/organizationQueries";
import { getApiErrorMessage } from "../../../utils/apiError";
import { BranchTreeDialog } from "../../settings/master-data/OrgTreeDialogs";
import { AddressDialog } from "./CompanyDialogs";

const EMPTY = [];
const TABS = [
  ["details", "Details"],
  ["addresses", "Addresses"],
  ["branches", "Branches"],
];

/** Tax ID, registration number, main contact (PUT /companies/:id/details). */
const DetailsTab = ({ company, canModify }) => {
  const query = useCompanyDetails(company.id);
  const save = useSaveCompanyDetails();
  const [form, setForm] = useState({
    taxId: "",
    registrationNumber: "",
    mainContact: "",
  });
  useEffect(() => {
    const d = query.data || {};
    setForm({
      taxId: d.taxId || "",
      registrationNumber: d.registrationNumber || "",
      mainContact: d.mainContact || "",
    });
  }, [query.data]);
  const submit = (e) => {
    e.preventDefault();
    save.mutate(
      {
        id: company.id,
        taxId: form.taxId.trim() || null,
        registrationNumber: form.registrationNumber.trim() || null,
        mainContact: form.mainContact.trim() || null,
      },
      {
        onSuccess: () => toast.success("Company details saved."),
        onError: (err) =>
          toast.error(getApiErrorMessage(err, "Could not save the details.")),
      },
    );
  };
  if (query.isLoading)
    return (
      <div className="text-center py-4">
        <span
          className="spinner-border spinner-border-sm text-primary"
          role="status"
          aria-label="Loading"
        ></span>
      </div>
    );
  return (
    <form onSubmit={submit}>
      <fieldset disabled={!canModify} className="d-flex flex-column gap-3">
        {[
          ["taxId", "Tax ID (TIN)"],
          ["registrationNumber", "Registration number (SEC/DTI)"],
          ["mainContact", "Main contact"],
        ].map(([k, label]) => (
          <div key={k}>
            <label htmlFor={`cd-${k}`} className="profile-label">
              {label}
            </label>
            <input
              id={`cd-${k}`}
              className="form-control"
              value={form[k]}
              onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
            />
          </div>
        ))}
      </fieldset>
      {canModify && (
        <div className="d-flex justify-content-end mt-3">
          <button
            type="submit"
            className="btn btn-primary profile-btn"
            disabled={save.isPending}
          >
            {save.isPending && (
              <span
                className="spinner-border spinner-border-sm"
                aria-hidden="true"
              ></span>
            )}
            Save details
          </button>
        </div>
      )}
    </form>
  );
};

/** Main / Billing / Shipping addresses. */
const AddressesTab = ({ company, canModify }) => {
  const query = useCompanyAddresses(company.id);
  const remove = useDeleteCompanyAddress();
  const save = useSaveCompanyAddress();
  const [editing, setEditing] = useState(null); // {} new, address = edit
  const [deleting, setDeleting] = useState(null);
  const list = query.data || EMPTY; // plain array

  return (
    <>
      {canModify && (
        <div className="d-flex justify-content-end mb-2">
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={() => setEditing({})}
          >
            <i className="bi bi-plus-lg me-1"></i>Add address
          </button>
        </div>
      )}
      {query.isLoading && (
        <div className="text-center py-4">
          <span
            className="spinner-border spinner-border-sm text-primary"
            role="status"
            aria-label="Loading"
          ></span>
        </div>
      )}
      {query.isError && (
        <div className="alert alert-danger fs-085">
          {getApiErrorMessage(query.error, "Could not load the addresses.")}
        </div>
      )}
      {!query.isLoading && list.length === 0 && (
        <p className="fs-085 text-slate-500">No addresses yet.</p>
      )}
      <div className="d-flex flex-column gap-2">
        {list.map((a) => (
          <div
            key={a.id}
            className={`company-address ${a.isActive === false ? "is-inactive" : ""}`}
          >
            <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
              <strong className="fs-085">{a.type || "Main"}</strong>
              {a.isPrimary && (
                <span className="status-pill status-pill--blue">Primary</span>
              )}
              {a.isActive === false && (
                <span className="status-pill status-pill--gray">Inactive</span>
              )}
              {canModify && (
                <span className="ms-auto d-flex gap-1">
                  {!a.isPrimary && (
                    <button
                      type="button"
                      className="btn btn-sm btn-link p-0 me-2"
                      onClick={() =>
                        save.mutate(
                          { addressId: a.id, isPrimary: true },
                          {
                            onError: (err) =>
                              toast.error(
                                getApiErrorMessage(err, "Could not update."),
                              ),
                          },
                        )
                      }
                    >
                      Make primary
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-sm btn-light border btn-icon-28"
                    onClick={() => setEditing(a)}
                    aria-label="Edit address"
                  >
                    <i className="bi bi-pencil"></i>
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-light border btn-icon-28 text-danger"
                    onClick={() => setDeleting(a)}
                    aria-label="Delete address"
                  >
                    <i className="bi bi-trash"></i>
                  </button>
                </span>
              )}
            </div>
            <div className="fs-080 text-slate-700">
              {[a.addressLine1, a.addressLine2].filter(Boolean).join(", ")}
              <br />
              {[a.city, a.province, a.state, a.postalCode]
                .filter(Boolean)
                .join(", ")}
            </div>
            {(a.phoneNumber || a.email || a.faxNumber || a.supportEmail) && (
              <div className="fs-075 text-slate-500 mt-1">
                {[
                  a.phoneNumber && `☎ ${a.phoneNumber}`,
                  a.faxNumber && `Fax ${a.faxNumber}`,
                  a.email,
                  a.supportEmail && `Support: ${a.supportEmail}`,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            )}
          </div>
        ))}
      </div>
      {editing && (
        <AddressDialog
          companyId={company.id}
          address={editing.id ? editing : null}
          onClose={() => setEditing(null)}
        />
      )}
      {deleting && (
        <SafeDeleteDialog
          title="Delete address?"
          name={`${deleting.type || "Main"} address`}
          onDelete={() => remove.mutateAsync(deleting.id)}
          onClose={() => setDeleting(null)}
        />
      )}
    </>
  );
};

/** Company drawer: details, addresses, branches. */
const CompanyDrawer = ({ company, canModify, onClose }) => {
  const [tab, setTab] = useState("details");
  const [showTree, setShowTree] = useState(false);
  useEffect(() => {
    if (showTree) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, showTree]);

  return (
    <div className="app-drawer-backdrop" onClick={onClose}>
      <aside
        className="app-drawer d-flex flex-column"
        role="dialog"
        aria-modal="true"
        aria-labelledby="company-drawer-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="app-dialog-header">
          <div>
            <h5 id="company-drawer-title" className="fw-bold text-navy m-0">
              {company.companyName}
            </h5>
            <div className="d-flex flex-wrap align-items-center gap-2 mt-1 fs-080 text-slate-500">
              {company.type && <span>{company.type}</span>}
              {company.region && <span>· {company.region}</span>}
              {company.isActive === false && (
                <span className="status-pill status-pill--gray">Inactive</span>
              )}
            </div>
          </div>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={onClose}
          ></button>
        </div>
        <div className="app-drawer-body">
          <div className="map-tabs" role="tablist">
            {TABS.map(([k, label]) => (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={tab === k}
                className={`map-tab ${tab === k ? "active" : ""}`}
                onClick={() => setTab(k)}
              >
                {label}
              </button>
            ))}
          </div>
          {tab === "details" && (
            <DetailsTab company={company} canModify={canModify} />
          )}
          {tab === "addresses" && (
            <AddressesTab company={company} canModify={canModify} />
          )}
          {tab === "branches" && (
            <div>
              <p className="fs-085 text-slate-600">
                {company.branchCount ?? 0} branch(es) ·{" "}
                {company.employeeCount ?? 0} employee(s) here,{" "}
                {company.totalEmployeeCount ?? 0} including sub-companies.
              </p>
              <button
                type="button"
                className="btn btn-outline-primary profile-btn bg-white"
                onClick={() => setShowTree(true)}
              >
                <i className="bi bi-diagram-3"></i> View branch tree
              </button>
            </div>
          )}
        </div>
      </aside>
      {showTree && (
        <BranchTreeDialog
          companyId={company.id}
          onClose={() => setShowTree(false)}
        />
      )}
    </div>
  );
};

export default CompanyDrawer;
